import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AccountingEventsGroup,
  CanonicalBusinessEvent,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types";
import { isDigitalDocument, isDocumentFlowCbe } from "../helpers/typeChecks";
import { shouldHideAccountingFlow as checkShouldHideAccountingFlow, isRevenueBookedByBothFlows, FLOW_TYPES, FlowType } from "../helpers/flowTypeUtils";

interface UseFlowTypeProps {
  eventData?: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[];
  cbeData?: CanonicalBusinessEvent[];
  glProduct?: string;
}

interface UseFlowTypeResult {
  flowType: FlowType;
  setFlowType: (newFlowType: FlowType) => void;
  filteredEvents: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[];
  filteredCbes: CanonicalBusinessEvent[];
  showFlowTypeSelector: boolean;
  shouldHideAccountingFlow: boolean;
  documentEvents: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[];
  accountingEvents: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[];
  documentCbes: CanonicalBusinessEvent[];
  accountingCbes: CanonicalBusinessEvent[];
}

export const FLOW_PARAM = "flowType";

/**
 * Custom hook to manage flow type selection and filtering
 */
export function useFlowType({
  eventData = [],
  cbeData = [],
  glProduct = "",
}: UseFlowTypeProps): UseFlowTypeResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-filter events and CBEs by type
  const { documentEvents, accountingEvents, documentCbes, accountingCbes } =
    useMemo(() => {
      return {
        documentEvents: eventData.filter(isDigitalDocument),
        accountingEvents: eventData.filter(
          (event) => !isDigitalDocument(event)
        ),
        documentCbes: cbeData.filter(isDocumentFlowCbe),
        accountingCbes: cbeData.filter((cbe) => !isDocumentFlowCbe(cbe)),
      };
    }, [eventData, cbeData]);

  // Calculate derived state
  const { hasDocumentFlow, hasAccountingFlow, shouldHideAccountingFlow } =
    useMemo(() => {
      const documentFlowExists =
        documentEvents.length > 0 || documentCbes.length > 0;
      const accountingFlowExists =
        accountingEvents.length > 0 || accountingCbes.length > 0;
      const hideAccountingFlow = checkShouldHideAccountingFlow(
        eventData,
        cbeData
      );

      return {
        hasDocumentFlow: documentFlowExists,
        hasAccountingFlow: accountingFlowExists,
        shouldHideAccountingFlow: hideAccountingFlow,
      };
    }, [
      documentEvents,
      documentCbes,
      accountingEvents,
      accountingCbes,
      eventData,
      cbeData,
    ]);

  // Determine if we should show the flow selector
  const showFlowTypeSelector = useMemo(() => {
    return !shouldHideAccountingFlow && hasDocumentFlow && hasAccountingFlow;
  }, [shouldHideAccountingFlow, hasDocumentFlow, hasAccountingFlow]);

  // Determine default flow type based on available data
  const defaultFlowType = useMemo<FlowType>(() => {
    if (shouldHideAccountingFlow || (hasDocumentFlow && !hasAccountingFlow)) {
      return FLOW_TYPES.DOCUMENT;
    }

    // When revenue is booked by both flows, default to document flow
    if (hasDocumentFlow && hasAccountingFlow && isRevenueBookedByBothFlows(eventData, cbeData)) {
      return FLOW_TYPES.DOCUMENT;
    }

    return FLOW_TYPES.ACCOUNTING; // Default to accounting for other cases (legacy flows, etc.)
  }, [shouldHideAccountingFlow, hasDocumentFlow, hasAccountingFlow, eventData, cbeData]);

  const urlFlowType = searchParams.get(FLOW_PARAM) as FlowType | null;

  // User's selected flow type (null means use default)
  const [selectedFlowType, setSelectedFlowType] = useState<FlowType | null>(
    urlFlowType
  );

  // Update URL when selected flow type changes
  useEffect(() => {
    if (selectedFlowType && showFlowTypeSelector) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set(FLOW_PARAM, selectedFlowType);
          return newParams;
        },
        { replace: true } // dont add to browser history
      );
    }
  }, [selectedFlowType, showFlowTypeSelector, setSearchParams]);

  useEffect(() => {
    if (urlFlowType) {
      setSelectedFlowType(urlFlowType);
    }
  }, [urlFlowType]);

  // The actual flow type to use (selected or default)
  const flowType = selectedFlowType || defaultFlowType;

  // Filter events and CBEs based on the current flow type
  const filteredEvents = useMemo(() => {
    return flowType === FLOW_TYPES.DOCUMENT ? documentEvents : accountingEvents;
  }, [flowType, documentEvents, accountingEvents]);

  const filteredCbes = useMemo(() => {
    return flowType === FLOW_TYPES.DOCUMENT ? documentCbes : accountingCbes;
  }, [flowType, documentCbes, accountingCbes]);

  // Handler to change the flow type
  const setFlowType = useCallback((newFlowType: FlowType) => {
    setSelectedFlowType(newFlowType);
  }, []);

  return {
    flowType,
    setFlowType,
    filteredEvents,
    filteredCbes,
    showFlowTypeSelector,
    shouldHideAccountingFlow,
    documentEvents,
    accountingEvents,
    documentCbes,
    accountingCbes,
  };
}
